import { Avatar, type AvatarTone, cn, HandIcon, MicOffIcon } from "@rhema/ui";
import type { VideoTrack } from "livekit-client";
import { useEffect, useRef } from "react";
import { firstName, toneFor } from "../data/view";
import type { Tile } from "./media";

export const tileGround: Record<AvatarTone, string> = {
  blue: "bg-tone-blue",
  sky: "bg-tone-sky",
  sage: "bg-tone-sage",
  rose: "bg-tone-rose",
  sand: "bg-tone-sand",
  moss: "bg-tone-moss",
  stone: "bg-tone-stone",
  self: "bg-gold-tint",
};

export function TrackVideo({
  track,
  mirror,
  fit = "cover",
  label,
}: {
  track: VideoTrack;
  mirror?: boolean;
  fit?: "cover" | "contain";
  label: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted
      aria-label={label}
      className={cn(
        "absolute inset-0 h-full w-full rounded-[inherit]",
        fit === "cover" ? "object-cover" : "object-contain",
        mirror && "-scale-x-100",
      )}
    />
  );
}

/** One person in the call. The active speaker gets a blue ring. */
export function ParticipantTile({ tile, avatarSize = 92 }: { tile: Tile; avatarSize?: number }) {
  const tone = toneFor(tile.identity);
  const name = tile.local ? "You" : firstName(tile.name);
  return (
    <div
      data-testid="participant-tile"
      className={cn(
        "relative flex min-h-[120px] items-center justify-center overflow-hidden rounded-panel",
        tileGround[tone],
        tile.speaking && "shadow-[0_0_0_3px_var(--color-blue)]",
      )}
    >
      {tile.camera ? (
        <TrackVideo track={tile.camera} mirror={tile.local} label={`${name}'s camera`} />
      ) : (
        <span
          className={cn(
            "rounded-full",
            tile.speaking && "shadow-[0_0_0_10px_rgb(36_65_138/0.2),0_0_0_22px_rgb(36_65_138/0.08)]",
          )}
        >
          <Avatar
            name={tile.name}
            tone={tile.speaking ? "self" : tone}
            solid={!tile.speaking}
            serif
            size={avatarSize}
          />
        </span>
      )}
      <span className="absolute bottom-2.5 left-2.5 flex items-center gap-1.5 rounded-full bg-linen px-2.5 py-1.5 text-[13px] font-semibold">
        {tile.speaking && (
          <span className="flex items-center gap-0.5" aria-hidden="true">
            <span className="h-2 w-[3px] rounded-sm bg-blue" />
            <span className="h-[13px] w-[3px] rounded-sm bg-blue" />
            <span className="h-1.5 w-[3px] rounded-sm bg-blue" />
          </span>
        )}
        {!tile.micOn && <MicOffIcon size={13} strokeWidth={2.6} className="text-scarlet" label="Muted" />}
        {name}
        {tile.speaking && <span className="sr-only">, speaking</span>}
      </span>
      {tile.handRaised && (
        <span className="absolute top-2.5 right-2.5 flex items-center gap-1 rounded-full bg-blue px-[9px] py-[5px] text-xs font-semibold text-white">
          <HandIcon size={13} strokeWidth={2.4} />
          Hand raised
        </span>
      )}
    </div>
  );
}

/** Columns for a call grid of this many people, up to 32. */
export function gridColumns(count: number, desktop: boolean): number {
  if (count <= 1) return 1;
  if (count <= 4) return 2;
  if (count <= 9) return 3;
  if (!desktop) return 3;
  if (count <= 16) return 4;
  if (count <= 25) return 5;
  return 6;
}
